import React, { useEffect, useState } from 'react';

import { type Variant } from '../../types';
import { DayTimePicker } from '../day-time-picker';
import { type DayTimePickerValue } from '../day-time-picker/types';
import { FullWidthFlex } from '../styled';

import { type DayTimeRangePickerProps } from './types';

const rangeColumnSpacing: Record<Variant, number> = {
	default: 1.5,
	compact: 1,
};

export const DayTimeRangePicker: React.FC<DayTimeRangePickerProps> = ({
	labels,
	value,
	onChange,
	weekStartDay,
	isDisabled,
	timeIsEditable,
	timeSlotIndex,
	variant = 'default',
}) => {
	const [start, setStart] = useState<DayTimePickerValue | null>(
		value
			? {
					day: value.startDay,
					hour: value.startHour,
					minute: value.startMinute,
				}
			: null,
	);
	const [end, setEnd] = useState<DayTimePickerValue | null>(
		value
			? {
					day: value.endDay,
					hour: value.endHour,
					minute: value.endMinute,
				}
			: null,
	);

	const handleChangeStart = (values: DayTimePickerValue) => {
		setStart(values);

		const isAllValuesSet = !!(values?.day && end?.day);

		if (onChange && isAllValuesSet) {
			onChange({
				startDay: values.day,
				startHour: values.hour,
				startMinute: values.minute,
				endDay: end.day,
				endHour: end.hour,
				endMinute: end.minute,
			});
		}
	};

	const handleChangeEnd = (values: DayTimePickerValue) => {
		setEnd(values);

		const isAllValuesSet = !!(start?.day && values?.day);

		if (onChange && isAllValuesSet) {
			onChange({
				startDay: start.day,
				startHour: start.hour,
				startMinute: start.minute,
				endDay: values.day,
				endHour: values.hour,
				endMinute: values.minute,
			});
		}
	};

	useEffect(() => {
		if (value) {
			setStart({
				day: value.startDay,
				hour: value.startHour,
				minute: value.startMinute,
			});

			setEnd({
				day: value.endDay,
				hour: value.endHour,
				minute: value.endMinute,
			});
		}
	}, [value]);

	return (
		<FullWidthFlex columnGap={rangeColumnSpacing[variant]}>
			<DayTimePicker
				autoFocus={value?.isNew}
				weekStartDay={weekStartDay}
				value={start}
				onChange={(values) => handleChangeStart(values)}
				label={labels?.start}
				isDisabled={isDisabled}
				timeIsEditable={timeIsEditable}
				timeSlotIndex={timeSlotIndex}
				variant={variant}
			/>
			<DayTimePicker
				weekStartDay={weekStartDay}
				value={end}
				onChange={(values) => handleChangeEnd(values)}
				label={labels?.end}
				isDisabled={isDisabled}
				timeIsEditable={timeIsEditable}
				timeSlotIndex={timeSlotIndex}
				variant={variant}
			/>
		</FullWidthFlex>
	);
};
